import React, { useState, useEffect } from 'react';
import { Member } from '../../types';
import { createMember, updateMember, MemberCreate, MemberUpdate } from '../../services/memberService';
import Modal from '../shared/Modal';

interface MemberFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  member?: Member | null; // null/undefined = create mode
  corporateCode: string | number;
}

const MemberFormModal: React.FC<MemberFormModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  member,
  corporateCode,
}) => {
  const isEdit = !!member;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mainPhone, setMainPhone] = useState('');
  const [secondPhone, setSecondPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('United States');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [emergencyRelation, setEmergencyRelation] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (member) {
        setFirstName(member.firstName);
        setLastName(member.lastName);
        setEmail(member.email);
        setUsername(member.username);
        setPassword('');
        setMainPhone(member.phone || '');
        setSecondPhone(member.secondPhone || '');
        setAddress(member.address || '');
        setCity(member.city || '');
        setState(member.state || '');
        setZipCode(member.zipCode || '');
        setCountry(member.country || 'United States');
        setEmergencyName(member.emergencyName || '');
        setEmergencyPhone(member.emergencyPhone || '');
        setEmergencyRelation(member.emergencyRelation || '');
        setNotes(member.notes || '');
      } else {
        setFirstName('');
        setLastName('');
        setEmail('');
        setUsername('');
        setPassword('');
        setMainPhone('');
        setSecondPhone('');
        setAddress('');
        setCity('');
        setState('');
        setZipCode('');
        setCountry('United States');
        setEmergencyName('');
        setEmergencyPhone('');
        setEmergencyRelation('');
        setNotes('');
      }
      setError(null);
    }
  }, [isOpen, member]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !username.trim()) {
      setError('First name, last name, email, and username are required.');
      return;
    }

    if (!isEdit && !password.trim()) {
      setError('Password is required when creating a new member.');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && member) {
        const data: MemberUpdate = {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          username: username.trim(),
          main_phone: mainPhone.trim() || undefined,
          second_phone: secondPhone.trim() || undefined,
          address: address.trim() || undefined,
          city: city.trim() || undefined,
          state: state.trim() || undefined,
          zip_code: zipCode.trim() || undefined,
          country: country.trim() || undefined,
          emergency_name: emergencyName.trim() || undefined,
          emergency_phone: emergencyPhone.trim() || undefined,
          emergency_relation: emergencyRelation.trim() || undefined,
          notes: notes.trim() || undefined,
        };
        if (password.trim()) {
          data.password = password.trim();
        }
        await updateMember(member.id, data);
      } else {
        const data: MemberCreate = {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          username: username.trim(),
          password: password.trim(),
          corporate_code: corporateCode,
          main_phone: mainPhone.trim() || undefined,
          second_phone: secondPhone.trim() || undefined,
          address: address.trim() || undefined,
          city: city.trim() || undefined,
          state: state.trim() || undefined,
          zip_code: zipCode.trim() || undefined,
          country: country.trim() || undefined,
          emergency_name: emergencyName.trim() || undefined,
          emergency_phone: emergencyPhone.trim() || undefined,
          emergency_relation: emergencyRelation.trim() || undefined,
          notes: notes.trim() || undefined,
        };
        await createMember(data);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error('Error saving member:', err);
      setError(err.message || 'Failed to save member');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
        {saving ? 'Saving...' : isEdit ? 'Update Member' : 'Create Member'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Member' : 'Add Member'}
      size="large"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="form-modal-body">
        {error && <div className="form-error-banner">{error}</div>}

        <div className="form-row">
          <div className="form-group">
            <label>First Name *</label>
            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Username *</label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>{isEdit ? 'Password (leave blank to keep current)' : 'Password *'}</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required={!isEdit}
              placeholder={isEdit ? 'Leave blank to keep current' : ''}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Email *</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Phone</label>
            <input type="text" value={mainPhone} onChange={e => setMainPhone(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Secondary Phone</label>
            <input type="text" value={secondPhone} onChange={e => setSecondPhone(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Address</label>
          <input type="text" value={address} onChange={e => setAddress(e.target.value)} />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>City</label>
            <input type="text" value={city} onChange={e => setCity(e.target.value)} />
          </div>
          <div className="form-group">
            <label>State</label>
            <input type="text" value={state} onChange={e => setState(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Zip Code</label>
            <input type="text" value={zipCode} onChange={e => setZipCode(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Country</label>
          <input type="text" value={country} onChange={e => setCountry(e.target.value)} />
        </div>

        <h3 className="form-section-title">Emergency Contact</h3>

        <div className="form-row">
          <div className="form-group">
            <label>Name</label>
            <input type="text" value={emergencyName} onChange={e => setEmergencyName(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input type="text" value={emergencyPhone} onChange={e => setEmergencyPhone(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Relation</label>
            <input type="text" value={emergencyRelation} onChange={e => setEmergencyRelation(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
        </div>
      </form>
    </Modal>
  );
};

export default MemberFormModal;
